import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetWhatsAppGatewayStatus,
  GetWhatsAppActivityLogs,
  PostWhatsAppLogout,
  PostWhatsAppToggle,
  PostWhatsAppConnect,
  SendWhatsAppTextMessage,
  SendWhatsAppDocument,
  GetWhatsAppTenantTokens,
  GetWhatsAppTenantTokenUsage,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { STALE_TIME_0, DEFAULT_STALE_TIME } from "../Constant/AppConstants";
import { useAuthChecker } from "../Context";

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const whatsappQueryKeys = {
  gatewayStatus: () => ["whatsapp", "gateway-status"] as const,
  activityLogs: () => ["whatsapp", "activity-logs"] as const,
};

// ─── Connectivity Helper ────────────────

export const useWhatsAppConnected = (tenantId?: string): boolean => {
  const { user } = useAuthChecker();
  const activeTenantId = tenantId || user?.tenantId;
  const statusQuery = useWhatsAppGatewayStatusQuery(activeTenantId);
  const status = statusQuery.data?.status;
  return status === "READY";
};

// ─── Query Hooks ──────────────────────────────────────────────────────────────

export const useWhatsAppGatewayStatusQuery = (
  tenantId: string | undefined,
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    queryKey: [...whatsappQueryKeys.gatewayStatus(), tenantId],
    queryFn: () => GetWhatsAppGatewayStatus(tenantId!),
    enabled: !!tenantId,
    staleTime: STALE_TIME_0,
    refetchInterval: (query) => {
      const data = query.state.data;
      const status = data?.status;
      if (status === "QR" || status === "INITIALIZING") {
        return 2000;
      }
      return 4000;
    },
    refetchOnWindowFocus: true,
    retry: false,
    ...options,
  });
};

export const useWhatsAppActivityLogsQuery = (
  tenantId: string | undefined,
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    queryKey: [...whatsappQueryKeys.activityLogs(), tenantId],
    queryFn: () => GetWhatsAppActivityLogs(tenantId!),
    enabled: !!tenantId,
    staleTime: STALE_TIME_0,
    refetchInterval: 4000,
    refetchOnWindowFocus: true,
    retry: false,
    ...options,
  });
};

export const useWhatsAppTokensQuery = () => {
  return useQuery({
    queryKey: ["whatsapp-tokens"],
    queryFn: async () => unwrapApiResponse(await GetWhatsAppTenantTokens()),
    staleTime: DEFAULT_STALE_TIME,
  });
};

export const useWhatsAppTokenUsageQuery = () => {
  return useQuery({
    queryKey: ["whatsapp-tokens", "usage"],
    queryFn: async () => unwrapApiResponse(await GetWhatsAppTenantTokenUsage()),
    staleTime: DEFAULT_STALE_TIME,
  });
};

// ─── Mutation Hooks ───────────────────────────────────────────────────────────

export const useWhatsAppLogoutMutation = (tenantId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => PostWhatsAppLogout(tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...whatsappQueryKeys.gatewayStatus(), tenantId],
      });
    },
  });
};

export const useWhatsAppToggleMutation = (tenantId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => PostWhatsAppToggle(tenantId!, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...whatsappQueryKeys.gatewayStatus(), tenantId],
      });
    },
  });
};

export const useWhatsAppConnectMutation = (tenantId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => PostWhatsAppConnect(tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...whatsappQueryKeys.gatewayStatus(), tenantId],
      });
    },
  });
};

export const useSendWhatsAppTextMessageMutation = () => {
  return useMutation({
    mutationFn: (payload: Parameters<typeof SendWhatsAppTextMessage>[0]) =>
      SendWhatsAppTextMessage(payload),
  });
};

export const useSendWhatsAppDocumentMutation = () => {
  return useMutation({
    mutationFn: (payload: Parameters<typeof SendWhatsAppDocument>[0]) =>
      SendWhatsAppDocument(payload),
  });
};

// ─── Service Hook ─────────────────────────────────────────────────────────────

export const useWhatsAppService = (tenantId: string | undefined) => {
  const gatewayStatusQuery = useWhatsAppGatewayStatusQuery(tenantId);
  const activityLogsQuery = useWhatsAppActivityLogsQuery(tenantId);
  const logoutMutation = useWhatsAppLogoutMutation(tenantId);
  const toggleMutation = useWhatsAppToggleMutation(tenantId);
  const connectMutation = useWhatsAppConnectMutation(tenantId);
  const tokensQuery = useWhatsAppTokensQuery();
  const tokenUsageQuery = useWhatsAppTokenUsageQuery();

  return {
    gatewayStatusQuery,
    activityLogsQuery,
    logoutMutation,
    toggleMutation,
    connectMutation,
    tokensQuery,
    tokenUsageQuery,
    refetchStatus: gatewayStatusQuery.refetch,
    refetchLogs: activityLogsQuery.refetch,
  };
};
